import { workspaceRoleLabel } from '@/utils/workspaceRoleLabel';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Search, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { useAcceptInvitation, useActivateWorkspace, useFetchMyInvitations } from '@/hooksApi';
import { Button } from '@/components/ui/button';
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/use-toast';
import { useUserStore, useWorkspaceSwitchStore } from '@/stores';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import type { Workspace, WorkspaceInvitation } from '@/types';
import WorkspaceTile from './WorkspaceTile';
import CreateWorkspaceDialog from './CreateWorkspaceDialog';
import { duplicateNameTags } from './duplicateNameTags';

/** Above this many, the list stops being scannable and earns a filter. */
const SEARCHABLE_FROM = 6;
/** ⌘1…⌘9 — nine is where single-key addressing stops being memorable. */
const SHORTCUT_LIMIT = 9;
/** Below this the overlay reads as a flicker rather than as progress. */
const MIN_OVERLAY_MS = 250;

interface Props {
  collapsed: boolean;
}

/**
 * BOXY-44 — the workspace card, under the logo, in the shadcn layer.
 *
 * 🔴🔴 IT RENDERS FOR EVERY MERCHANT, INCLUDING SINGLE-WORKSPACE ONES (owner,
 * 2026-09-15). The previous version returned null below two workspaces, which
 * meant the overwhelming majority of merchants had no way to discover that a
 * second store was possible — and it took the LOGO'S SLOT for the few who did,
 * which the owner rejected on sight. The card sits under the brand now and the
 * brand never moves.
 *
 * ⭐ "New workspace" is ALWAYS RENDERED and DISABLED with a reason when the
 * caller cannot use it, never hidden. The owner's correction of my own earlier
 * reasoning, and he is right: a disabled control with "Ask Boxy to enable
 * multiple workspaces" teaches the merchant that the capability exists and how
 * to get it. Hiding it teaches them nothing, and a visible control that answers
 * 403 teaches them their account is broken.
 */
const WorkspaceSwitcher = ({ collapsed }: Props) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir() === 'rtl' ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const activate = useActivateWorkspace();
  const accept = useAcceptInvitation();
  const { data: invitationsData } = useFetchMyInvitations();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const switchingTo = useWorkspaceSwitchStore((state) => state.target);
  const beginSwitch = useWorkspaceSwitchStore((state) => state.begin);
  const endSwitch = useWorkspaceSwitchStore((state) => state.end);
  const searchRef = useRef<HTMLInputElement>(null);

  const roleLabel = (workspace?: Pick<Workspace, 'role' | 'role_names'> | null) =>
    workspaceRoleLabel(workspace, t);

  const memberships = useMemo(() => user?.workspaces ?? [], [user?.workspaces]);
  // One source of truth for the count, shared with the avatar dot and the
  // lower menu (delta 4) — three places deriving it separately is how they
  // start disagreeing.
  const invitations = invitationsData?.object ?? [];
  const pendingInvitations = invitations.length;

  // Trust the server's `active_workspace` over the list's own is_active flag:
  // on a tab still holding the previous token the two disagree, and this is the
  // one describing what is actually being served.
  const activeUid = user?.active_workspace ?? memberships.find((w) => w.is_active)?.uid;
  const tags = duplicateNameTags(memberships);
  const active = memberships.find((w) => w.uid === activeUid) ?? memberships[0];

  /*
   * Delta 2 — ACTIVE FIRST, THEN A–Z. Server order is company id, i.e. the
   * order the workspaces happened to be created in, which means nothing to the
   * person reading it and shifts as they create more. The active one is pinned
   * because it is the row most likely to be confirmed rather than clicked.
   *
   * ⚠️ ⌘n is assigned over THIS order, so the hints match what the eye sees.
   * Numbering the server's order would put ⌘2 on the third row.
   */
  const workspaces = useMemo(() => {
    const rest = memberships
      .filter((w) => w.uid !== activeUid)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    return active ? [active, ...rest] : rest;
  }, [memberships, activeUid, active]);

  const ownsActive = active?.role === 'OWNER';
  const canCreate = Boolean(user?.can_create_workspaces) && ownsActive;

  const switchTo = async (workspace: Workspace) => {
    if (!workspace || workspace.uid === activeUid || switchingTo) return;

    setOpen(false);
    // The label flips before the request so the UI answers the click
    // immediately; the overlay covers the gap and the label reverts on failure.
    beginSwitch(workspace);
    const startedAt = Date.now();

    try {
      await activate.mutateAsync(workspace.uid);
      navigate(ROUTE.ORDERS);
    } catch {
      toast({ title: String(t('workspace.switch_failed')), variant: 'destructive' });
    } finally {
      /*
       * Held for a minimum beat. A switch that resolves in 80ms flashes an
       * overlay the eye cannot resolve, which reads as a glitch rather than as
       * progress — and leaves the merchant unsure anything happened at all.
       */
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_OVERLAY_MS - elapsed);
      window.setTimeout(endSwitch, remaining);
    }
  };

  /**
   * Delta 6 — ACCEPTING JOINS, IT DOES NOT MOVE YOU.
   *
   * Switching swaps the token and clears the cache, so auto-switching on accept
   * would throw away whatever the merchant had open in the workspace they were
   * actually working in — a destructive side effect of a click that only said
   * "yes". The toast offers the switch instead, so the move stays deliberate.
   */
  const acceptFromMenu = async (invitation: WorkspaceInvitation) => {
    try {
      await accept.mutateAsync(invitation.uid);
      const name = invitation.workspace?.name ?? '';
      toast({
        title: String(t('workspace.joined', { name })),
        action: invitation.workspace?.uid ? (
          <ToastAction
            altText={String(t('workspace.switch_to'))}
            onClick={() => {
              const joined = (user?.workspaces ?? []).find(
                (w) => w.uid === invitation.workspace?.uid,
              );
              if (joined) void switchTo(joined);
            }}
          >
            {String(t('workspace.switch_to'))}
          </ToastAction>
        ) : undefined,
      });
    } catch {
      toast({ title: String(t('error')), variant: 'destructive' });
    }
  };

  /*
   * ⌘1…⌘9 / Ctrl+1…9 from anywhere.
   *
   * ⚠️ Ignored while focus is in a text field: a merchant typing an order note
   * must not be teleported into another workspace by a keystroke meant for
   * their text. `isContentEditable` covers rich-text editors, which report
   * their tag as DIV.
   */
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      const index = Number(event.key) - 1;
      if (
        Number.isNaN(index) ||
        index < 0 ||
        index >= Math.min(workspaces.length, SHORTCUT_LIMIT)
      ) {
        return;
      }

      event.preventDefault();
      void switchTo(workspaces[index]);
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  if (!active) return null;

  const searchable = workspaces.length >= SEARCHABLE_FROM;
  const shown = searchable
    ? workspaces.filter((w) => (w.name ?? '').toLowerCase().includes(query.trim().toLowerCase()))
    : workspaces;

  const displayed = switchingTo ?? active;

  const trigger = collapsed ? (
    <button
      type='button'
      className='relative flex w-full items-center justify-center rounded-lg p-1.5 transition-colors hover:bg-sd-muted data-[state=open]:bg-sd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary'
      aria-label={`${displayed.name ?? ''} · ${displayed.role ?? ''}`}
    >
      <WorkspaceTile uid={displayed.uid} name={displayed.name} />
      {pendingInvitations > 0 && (
        <span className='absolute end-1 top-1 h-2 w-2 rounded-full bg-sd-destructive ring-2 ring-sd-card' />
      )}
    </button>
  ) : (
    <button
      type='button'
      /*
       * QA v2-5 / addendum §11 — the brand yellow is a RING, never a fill
       * behind text: at ≈1.3:1 it fails contrast for the name sitting on it.
       * Hover and open use the sidebar accent; focus-visible uses the ring.
       */
      className='flex min-h-12 w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-start transition-colors hover:bg-sd-muted data-[state=open]:bg-sd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sd-card'
    >
      <WorkspaceTile uid={displayed.uid} name={displayed.name} />
      <span className='min-w-0 flex-1'>
        {/* BOXY-352: two lines, not one. 'QA BOXY-44 WS-B' and '…WS-C' cut
            at one line read as the same workspace; the tooltip has the rest. */}
        <bdi
          dir='auto'
          className='line-clamp-2 break-words text-sm font-medium leading-5 text-sd-foreground'
          title={displayed.name ?? undefined}
        >
          {displayed.name ?? String(t('workspace.untitled'))}
        </bdi>
        {/* Delta 1 — the count is the discovery affordance: a merchant with
            one workspace sees only their role, and one with several is told so
            without opening anything. */}
        {/* The ROLE alone. "Owner · 4 workspaces" truncated inside the card's
            210px, and a count the merchant cannot finish reading is worse than
            no count — the menu lists them all, one click away. */}
        <span className='block truncate text-xs leading-4 text-sd-muted-foreground'>
          {roleLabel(displayed)}
          {tags[displayed.uid] ? (
            <>
              {' · '}
              <bdi dir='ltr'>{tags[displayed.uid]}</bdi>
            </>
          ) : null}
        </span>
      </span>
      <ChevronsUpDown className='h-4 w-4 shrink-0 text-sd-muted-foreground' aria-hidden />
    </button>
  );

  return (
    <>
      {/* `dir` on the ROOT, not Content: Radix reads it there to mirror
          positioning and to flip arrow-key direction. The shadcn Content
          wrapper does not forward it. */}
      <DropdownMenu open={open} onOpenChange={setOpen} dir={dir}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          side='bottom'
          sideOffset={6}
          className='min-w-[--radix-dropdown-menu-trigger-width] w-[264px] rounded-[10px] border-sd-border p-1 shadow-lg sd-scope sd-font data-[state=open]:duration-150 data-[state=closed]:duration-100 ease-out motion-reduce:data-[state=open]:zoom-in-100 motion-reduce:data-[state=closed]:zoom-out-100 motion-reduce:data-[state=open]:slide-in-from-top-0 motion-reduce:data-[state=closed]:slide-out-to-top-0'
        >
          {/*
            Delta 4 — invitations live INSIDE the switcher, above the list.
            An invitation is a workspace you are one click from having, so the
            place someone looks for "which workspaces do I have" is the place it
            belongs. A separate screen makes a time-limited action something you
            have to go and find.
          */}
          {invitations.length > 0 && (
            <>
              <DropdownMenuLabel className='px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                {String(t('workspace.invitations'))} ({invitations.length})
              </DropdownMenuLabel>
              {invitations.map((invitation) => (
                <div
                  key={invitation.uid}
                  className='flex h-9 items-center gap-2.5 rounded-md px-2'
                  // Not a menu item: it contains its own buttons, and a
                  // menuitem that swallows Enter would fire the row instead of
                  // the button the user had focused.
                >
                  <WorkspaceTile
                    uid={invitation.workspace?.uid ?? invitation.uid}
                    name={invitation.workspace?.name}
                    size='sm'
                  />
                  <span className='min-w-0 flex-1 truncate text-sm'>
                    {invitation.workspace?.name ?? String(t('workspace.untitled'))}
                  </span>
                  <Button
                    size='sm'
                    variant='secondary'
                    className='h-7 shrink-0 px-2 text-xs'
                    disabled={accept.isLoading}
                    aria-label={`${String(t('workspace.accept'))} — ${invitation.workspace?.name ?? ''}`}
                    onClick={() => void acceptFromMenu(invitation)}
                  >
                    {String(t('workspace.accept'))}
                  </Button>
                </div>
              ))}
              <DropdownMenuSeparator className='my-1' />
            </>
          )}

          <DropdownMenuLabel className='px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
            {String(t('workspace.plural'))}
          </DropdownMenuLabel>

          {searchable && (
            <div className='px-2 pb-2'>
              <div className='relative'>
                <Search
                  className='pointer-events-none absolute top-2.5 h-3.5 w-3.5 text-sd-muted-foreground ltr:left-2 rtl:right-2'
                  aria-hidden
                />
                <Input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={String(t('workspace.search_placeholder'))}
                  className='h-8 text-sm ltr:pl-7 rtl:pr-7'
                  // The menu owns arrow-key navigation; without this the first
                  // keystroke moves the highlight instead of typing.
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          <DropdownMenuRadioGroup value={activeUid ?? ''}>
            {shown.map((workspace) => {
              const index = workspaces.indexOf(workspace);
              const isActive = workspace.uid === activeUid;

              return (
                /*
                 * Delta 2/12 — a RADIO item, not a plain one. "Which workspace am
                 * I in" is a single-choice question, and `menuitemradio` is what
                 * tells a screen reader that: the active row announces itself as
                 * checked instead of the check mark being a decorative tick only
                 * sighted users can see.
                 */
                <DropdownMenuRadioItem
                  key={workspace.uid}
                  value={workspace.uid}
                  onSelect={() => void switchTo(workspace)}
                  /*
                   * ⚠️ `[&>span:first-child]:hidden` — the shared RadioItem
                   * reserves `pl-8` and paints its Circle indicator ABSOLUTELY
                   * at `left-2`, which lands ON the tile (a black dot over the
                   * initials) and cannot mirror under RTL. The state is carried
                   * by the Check below instead, at the LOGICAL end.
                   */
                  className={cn(
                    'min-h-9 gap-2.5 rounded-md px-2 py-1.5 [&>span:first-child]:hidden',
                    isActive && 'font-medium',
                  )}
                >
                  <WorkspaceTile uid={workspace.uid} name={workspace.name} size='sm' />
                  <bdi
                    dir='auto'
                    className='line-clamp-2 min-w-0 flex-1 break-words'
                    title={workspace.name ?? undefined}
                  >
                    {workspace.name ?? String(t('workspace.untitled'))}
                  </bdi>
                  {tags[workspace.uid] ? (
                    <bdi dir='ltr' className='shrink-0 text-[11px] text-sd-muted-foreground'>
                      {tags[workspace.uid]}
                    </bdi>
                  ) : null}
                  <span className='flex h-5 shrink-0 items-center rounded-full border border-sd-border px-1.5 text-[11px] font-medium text-sd-muted-foreground'>
                    {roleLabel(workspace)}
                  </span>
                  {index < SHORTCUT_LIMIT && (
                    // dir=ltr: ⌘2 is a key combination, not prose, and mirrors
                    // into a nonsense "2⌘" under RTL otherwise.
                    <kbd
                      dir='ltr'
                      className='min-w-6 shrink-0 text-end text-[11px] text-sd-muted-foreground'
                    >
                      ⌘{index + 1}
                    </kbd>
                  )}
                  {isActive && <Check className='ms-auto h-4 w-4 shrink-0' aria-hidden />}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>

          {searchable && shown.length === 0 && (
            <p className='px-3 py-4 text-center text-[13px] text-sd-muted-foreground'>
              {String(t('workspace.no_matches'))}
            </p>
          )}

          <DropdownMenuSeparator className='my-1' />

          <DropdownMenuItem onSelect={() => navigate(ROUTE.SETTINGS_WORKSPACES)} className='gap-2'>
            <Settings2 className='h-4 w-4 shrink-0 text-sd-muted-foreground' aria-hidden />
            {String(t('workspace.manage'))}
          </DropdownMenuItem>

          {/* ALWAYS rendered. Disabled carries the reason; hidden would carry
              nothing, and enabled-then-403 would carry the wrong one. */}
          {canCreate ? (
            <DropdownMenuItem
              onSelect={() => setCreating(true)}
              className='h-9 gap-2.5 rounded-md px-2'
            >
              <Plus className='h-4 w-4 shrink-0 text-sd-muted-foreground' aria-hidden />
              {String(t('workspace.new'))}
            </DropdownMenuItem>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled DropdownMenuItem does not fire pointer events,
                      so the tooltip would never open on the control it
                      describes. The span is what the tooltip listens to. */}
                  <span className='block'>
                    <DropdownMenuItem disabled className='h-9 gap-2.5 rounded-md px-2'>
                      <Plus className='h-4 w-4 shrink-0 text-sd-muted-foreground' aria-hidden />
                      {String(t('workspace.new'))}
                    </DropdownMenuItem>
                  </span>
                </TooltipTrigger>
                <TooltipContent side='right'>{String(t('workspace.ask_boxy'))}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceDialog open={creating} onOpenChange={setCreating} onCreated={switchTo} />
    </>
  );
};

/**
 * Covers the gap between "clicked" and "the new workspace's data has landed".
 *
 * ⚠️ THE CONTENT AREA ONLY — the sidebar stays visible and is ALREADY showing
 * the target workspace, which is the most reassuring thing on the screen. The
 * first version covered the whole viewport including the sidebar, so the one
 * element that told the merchant the switch was working was the element it
 * hid. Anchored to the main region rather than the viewport.
 *
 * Held for a minimum beat by the caller: a flash shorter than the eye can
 * resolve reads as a glitch rather than as progress.
 */
/**
 * What the app shows while a workspace switch is in flight.
 *
 * The previous version was a dimmed, blurred page behind a centred card. That
 * is the vocabulary of a MODAL — "you are blocked, wait" — and a switch is not
 * a modal, it is navigation. It also blurred the very data that is about to be
 * replaced, which is both pointless and slightly alarming: the numbers the
 * merchant was reading go soft and unreadable for a second.
 *
 * What ships instead is what every product that does this well converged on:
 *
 * - ONE moving thing — a hairline indeterminate bar at the top edge. Motion
 *   that says "working" belongs in exactly one place; two or three competing
 *   pulses is what made the old one feel cheap.
 * - The DESTINATION's identity shown immediately (its tile and its name), so
 *   the switch reads as already arrived and merely filling in.
 * - A skeleton of the page shape underneath, on an OPAQUE background. Nothing
 *   is dimmed, because nothing behind it is still true.
 * - A 150ms entry delay, so a fast switch never flashes a loader at all. The
 *   worst loading state is one that appears and vanishes before it is read.
 *
 * Honest about motion preferences: with `prefers-reduced-motion` the bar stops
 * travelling and simply sits full-width, and the skeleton stops breathing. The
 * state is still legible — it is announced and it is written on screen — it
 * just stops moving.
 */
export const WorkspaceSwitchOverlay = () => {
  const workspace = useWorkspaceSwitchStore((state) => state.target);
  const { t } = useTranslation();

  if (!workspace) return null;

  return (
    <div
      role='status'
      aria-busy='true'
      aria-live='polite'
      /* fill-mode-both + delay-150 keeps it fully transparent for the first
         150ms, so a switch that resolves quickly never paints a loader. */
      className='absolute inset-0 z-50 flex animate-in flex-col overflow-hidden bg-sd-background fill-mode-both delay-150 fade-in duration-200'
    >
      {/* The one moving element on the screen. */}
      <div className='relative h-[2px] w-full shrink-0 overflow-hidden bg-sd-muted'>
        <div className='absolute inset-y-0 w-[40%] animate-ws-progress rounded-full bg-sd-primary motion-reduce:start-0 motion-reduce:w-full motion-reduce:animate-none' />
      </div>

      <div className='flex flex-1 flex-col gap-6 overflow-hidden p-6'>
        {/* Destination identity — real tile, real name, at the size and place
            the page header will occupy once it arrives. */}
        <div className='flex items-center gap-3'>
          <WorkspaceTile uid={workspace.uid} name={workspace.name} size='lg' />
          <div className='flex min-w-0 flex-col gap-1'>
            <bdi dir='auto' className='truncate text-base font-semibold text-sd-foreground'>
              {workspace.name}
            </bdi>
            <span className='truncate text-[13px] text-sd-muted-foreground'>
              {String(t('workspace.switching_detail'))}
            </span>
          </div>
        </div>

        {/* One shared breath on the wrapper rather than a pulse per block: the
            skeleton reads as a single surface settling, not as a dozen
            independent things flickering. */}
        <div
          aria-hidden
          data-testid='workspace-switch-skeleton'
          className='flex flex-1 animate-pulse flex-col gap-4 motion-reduce:animate-none'
        >
          <div className='flex items-center gap-2'>
            <div className='h-9 w-64 rounded-md bg-sd-muted' />
            <div className='h-9 w-28 rounded-md bg-sd-muted' />
            <div className='h-9 w-28 rounded-md bg-sd-muted' />
            <div className='ms-auto h-9 w-36 rounded-md bg-sd-muted' />
          </div>

          <div className='flex-1 overflow-hidden rounded-lg border border-sd-border'>
            <div className='h-11 w-full bg-sd-muted/60' />
            {Array.from({ length: 7 }).map((_, index) => (
              <div
                key={index}
                className='flex items-center gap-4 border-t border-sd-border px-4 py-3.5'
              >
                <div className='h-4 w-4 shrink-0 rounded bg-sd-muted' />
                <div className='h-4 w-28 rounded bg-sd-muted' />
                <div className='h-4 w-40 rounded bg-sd-muted' />
                <div className='h-4 w-24 rounded bg-sd-muted' />
                <div className='ms-auto h-5 w-20 rounded-full bg-sd-muted' />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Names the destination for a screen reader. The skeleton above is
          aria-hidden, so this is the only thing announced. */}
      <span className='sr-only'>
        {String(t('workspace.switching_to', { name: workspace.name ?? '' }))}
      </span>
    </div>
  );
};

export default WorkspaceSwitcher;
